<template>
  <div class="section-subportions h-100">
    <div class="h-100 d-flex flex-column justify-content-between">
      <div class="d-flex flex-column flex-grow-1 h-100">
        <b-row class="d-flex align-items-center mb-2 mt-1 h-auto">
          <b-col class="portion-block">
            <template v-if="viewToggle">
              <i
                class="far fa-chevron-right mr-2 dark arrow"
                :class="{ active: activeArrow }"
                aria-hidden="true"
                style="width: 10px"
                @click="onSubportionsTitleArrowClick"
              />
            </template>
            <span
              class="ap-title ap-title__main"
              @click="onSubportionsTitleClick"
            >{{ $t('diagnosis.sections.subportions') }}</span>
          </b-col>
          <b-col
            cols="auto"
            class="d-flex justify-content-end"
          >
            <div>
              <b-button
                class="btn__icon btn__icon--primary px-2"
                @click="viewToggle=!viewToggle"
              >
                <i
                  v-if="viewToggle"
                  v-tooltip="$t('diagnosis.btns.gridView')"
                  qa="section-subportions-grid-view-i"
                  class="fal fa-table"
                  aria-hidden="true"
                />
                <i
                  v-if="!viewToggle"
                  v-tooltip="$t('diagnosis.btns.thumbView')"
                  qa="section-subportions-thumbnails-view-i"
                  class="far fa-images"
                  aria-hidden="true"
                />
              </b-button>
            </div>
          </b-col>
        </b-row>
        <b-row
          v-if="!viewToggle"
          class="flex-grow-1"
        >
          <b-col>
            <vt-grid
              class="subportion-grid h-100"
              qa="section-subportions-grid"
              :column-defs="columnDefs"
              :row-data="rowData"
              :grid-options="gridOptions"
            />
          </b-col>
        </b-row>
        <div
          v-if="viewToggle"
          class="subportions-scroll px-1 flex-grow-1"
        >
          <div
            v-for="(portion, indexPortion) in portions"
            :key="indexPortion"
            qa="section-subportions-portion-collapse-div"
          >
            <b-row class="d-flex align-items-center portion-block">
              <b-col
                class="d-flex align-items-center"
                :class="portion.showBlock ? 'collapsed' : null"
                cols="auto"
                :aria-expanded="portion.showBlock ? 'true' : 'false'"
                :aria-controls="portion.id.toString()"
                @click="togglePortion(portion)"
              >
                <div>
                  <i
                    v-if="portion.showBlock"
                    class="far fa-chevron-down mr-2 dark"
                    style="width: 10px"
                    aria-hidden="true"
                  />
                  <i
                    v-else
                    class="far fa-chevron-right mr-2 dark"
                    style="width: 10px"
                    aria-hidden="true"
                  />
                </div>
                <div class="d-flex align-items-center">
                  <i
                    v-if="portion.depleted"
                    v-tooltip="$t('grid.detail.depletedBlock')"
                    class="mr-1 fal icon-block-slash main"
                    aria-hidden="true"
                  />
                  <i
                    v-else
                    class="mr-1 fal icon-block main"
                    aria-hidden="true"
                  />
                  <span
                    class="primary-color"
                    qa="section-subportions-portion-label-span"
                  >{{ `${portion.label} (${portion.subportions.length} ${$tc('diagnosis.labels.subportion', 2)})` }}</span>
                </div>
              </b-col>
              <b-col class="px-0">
                <hr>
              </b-col>
              <b-col
                cols="auto"
                class="d-flex justify-content-end"
              >
                <div class="d-flex align-items-center">
                  <i
                    v-if="portion.currentLaboratory.labId !== labId"
                    v-tooltip="`${$t('global.elementLocatedIn')} ${portion.currentLaboratory.labName}`"
                    aria-hidden="true"
                    class="fas fa-map-marker-alt alt-color px-2"
                  />
                  <b-button
                    v-tooltip="$tc('global.technique', 2)"
                    qa="section-subportions-assays-button"
                    class="btn__icon btn__icon--primary px-2"
                    @click="showAssayModal(portion.label)"
                  >
                    <i
                      class="fas fa-flask"
                      aria-hidden="true"
                    />
                  </b-button>
                  <!-- TODO: Oculto por petición de Javier -->
                  <!--<b-button class="btn__icon btn__icon--primary px-2" v-tooltip="$t('diagnosis.btns.qualityUse')">
                                      <i class="far fa-shield-check" aria-hidden="true"></i>
                                    </b-button>-->
                  <template v-if="portion.revisions.length">
                    <button
                      id="pop-review-portion"
                      qa="section-subportions-review-button"
                      class="btn btn-round p-0"
                      type="button"
                      :class="[allEntityRevisionsCompleted(portion.revisions) ? 'completed' : 'pending']"
                      @click="onShowRevisionPopoverClick(portion, ENTITY_TYPE_PORTION, portion.revisions)"
                    >
                      <i
                        v-tooltip="{ title: allEntityRevisionsCompleted(portion.revisions) ? $tc('diagnosis.labels.reviewCompleted', 1) : $tc('diagnosis.labels.pendingReview') }"
                        class="far fa-tasks"
                        aria-hidden="true"
                      />
                    </button>
                    <b-popover
                      v-if="portion.revisions.length > 1"
                      :show.sync="showRevisionsPopover"
                      target="pop-review-portion"
                      placement="top"
                      triggers="click blur"
                    >
                      <div class="review-pop">
                        <ul>
                          <li
                            v-if="portion.revisions.find(revision => !revision.finished)"
                            @click="mapAndShowRevisionInfo(portion, ENTITY_TYPE_PORTION, portion.revisions[0])"
                          >
                            {{ $t('diagnosis.labels.reviewInformation') }}
                          </li>
                          <template v-if="portion.revisions.filter(revision => revision.finished).length">
                            <hr class="my-0">
                            <div class="p-1 mx-3">
                              <span class="main">{{ $tc('diagnosis.labels.reviewCompleted', 2) }}</span>
                            </div>
                            <li
                              v-for="(revision, index) in portion.revisions.filter(aRevision => aRevision.finished)"
                              :key="revision.id"
                              @click="mapAndShowRevisionInfo(portion, ENTITY_TYPE_PORTION, revision)"
                            >
                              {{ $t('diagnosis.labels.review') }} {{ index + 1 }}
                            </li>
                          </template>
                        </ul>
                      </div>
                    </b-popover>
                  </template>
                  <b-button
                    :id="portion.label"
                    qa="section-subportions-ellipsis-button"
                    class="btn__icon btn__icon--primary px-2"
                  >
                    <i
                      class="fas fa-ellipsis-v"
                      aria-hidden="true"
                    />
                  </b-button>
                  <b-popover
                    :target="portion.label"
                    placement="bottom"
                    triggers="click blur"
                  >
                    <div class="ap-popover">
                      <ul>
                        <li
                          v-if="canRequestReview(portion)"
                        >
                          <b-button
                            qa="section-subportions-request-review-a"
                            @click="showReviewModal(portion, ENTITY_TYPE_PORTION)"
                          >
                            {{ $t('diagnosis.btns.review') }}
                          </b-button>
                        </li>
                        <!-- TODO: Oculto por petición de Javier -->
                        <!--<li class="py-1">
                                                          <a type="button" class="linkbtn">
                                                              <i class="far fa-envelope mr-2" aria-hidden="true"></i>
                                                              {{$t('diagnosis.btns.issueNotification')}}
                                                          </a>
                                                    </li>-->
                        <li
                          v-if="$_canCreateIncident()"
                        >
                          <b-button
                            qa="section-subportions-incident-a"
                            :disabled="!$_canBeIncidentCreatedToPortion(portion)"
                            @click="$emit('openIncidentFormModal', { coreEntity: 'PORTION', entitiesIds: {casId, samId, porId: portion.id}})"
                          >
                            {{ $t('diagnosis.btns.incident') }}
                          </b-button>
                        </li>
                        <li
                          v-if="$_canAttachDocumentToPortion()"
                        >
                          <b-button
                            qa="section-subportions-attachment-a"
                            :disabled="!$_canBeDocumentAttachedToPortion(portion)"
                            @click="$emit('openAddFileModalForPortion', [labId, casId, samId, portion.id ])"
                          >
                            {{ $t('diagnosis.btns.attach') }}
                          </b-button>
                        </li>
                        <li>
                          <b-button
                            qa="section-subportions-global-locator"
                            @click="showInGlobalLocator(portion.label)"
                          >
                            {{ $tc('global.showInGlobalLocator') }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </b-popover>
                </div>
              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <b-collapse
                  :id="portion.id.toString()"
                  v-model="portion.showBlock"
                >
                  <b-row class="pt-2 pb-4">
                    <b-col
                      v-if="!portion.subportions.length"
                      class="empty-subportions-text"
                    >
                      <span>{{ $t('diagnosis.emptySubportions') }}</span>
                    </b-col>
                    <b-col
                      v-for="subportion in portion.subportions"
                      :key="subportion.id"
                      qa="section-subportions-item-div"
                      xl="3"
                      :class="{'col-lg-4': !caseCollapsed, 'col-lg-6': caseCollapsed}"
                      class="mb-3"
                    >
                      <subportion-card
                        :subportion-data="subportion"
                        :show-portion="portion.showBlock"
                        :lab-id="labId"
                        :cas-id="casId"
                        :sam-id="samId"
                        :por-id="portion.id"
                        :case-collapsed="caseCollapsed"
                        @openAnalysisSidePanel="onOpenAnalysisSidePanel"
                        @openIncidentFormModal="$emit('openIncidentFormModal', $event)"
                        @openAddFileModal="$emit('openAddFileModalForSubportion', $event)"
                        @showSubportionAttachmentTreeModal="$emit('showSubportionAttachmentTreeModal', $event)"
                        @showIncidentsModalForSubportion="$emit('showIncidentsModalForSubportion', {coreEntity: 'SUBPORTION', casId, samId, porId: portion.id, supId: $event})"
                        @showReviewModal="onShowReviewModal(portion, subportion)"
                        @showReviewInfoModal="showInfoReviewModal($event)"
                        @reviewRequested="loadPortions"
                      />
                    </b-col>
                  </b-row>
                </b-collapse>
              </b-col>
            </b-row>
            <subportion-analysis-side-panel
              v-if="visibleSubportionAnalysisSidePanel"
              :subportion-analysis-result="selectedSubportionAnalysisResult"
              :subportion-label="selectedSubportionLabel"
              @close-side-panel="onSubportionAnalysisSidePanelClose"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VtGrid from '@/components/VtGrid/VtGrid'
import columnsSubportionGrid from '@/modules/diagnosisModule/views/form/config/columnsSubportionGrid'
import SubportionCard from '@/modules/diagnosisModule/views/form/SubportionCard'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import subportionService from '@/modules/diagnosisModule/services/subportionService'
import { find } from 'lodash'
import DiagnosisPortionService from '@/modules/diagnosisModule/services/DiagnosisPortionService'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import portionRevisionServices from '@/api/resources/common/portionRevisionServices'
import { PortionOperationGuardMixin } from '@/api/operations/mixins/guards/portion-operation-guard.mixin'
import { SubportionOperationGuardMixin } from '@/api/operations/mixins/guards/subportion-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { BROADCAST_ATTACHMENT_UPDATED_CHANNEL } from '@/modules/viewerModule/ViewerModule.models'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import SubportionAnalysisSidePanel
    from '@/modules/diagnosisModule/views/form/samplesView/sections/subportions/components/subportion-analysis-side-panel.vue'

export default {
    name: 'SectionSubportions',
    components: {
        SubportionAnalysisSidePanel,
        VtGrid,
        SubportionCard
    },
    mixins: [
        PortionOperationGuardMixin,
        SubportionOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    props: {
        labId: { required: true, type: Number },
        casId: { required: true, type: Number },
        samId: { required: true, type: Number },
        caseCollapsed: { type: Boolean }
    },
    data() {
        return {
            showRevisionsPopover: false,
            viewToggle: true,
            columnDefs: columnsSubportionGrid(),
            portions: [],
            gridApi: null,
            rowData: [],
            portionHaveUnfinishedRevisions: null,
            broadCastAttachmentUpdateChannel: null,
            activeArrow: false,
            visibleSubportionAnalysisSidePanel: false,
            selectedSubportionAnalysisResult: [],
            selectedSubportionLabel: null
        }
    },
    computed: {
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                onGridReady: params => this.onGridReady(params),
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                paginationAutoPageSize: false,
                getContextMenuItems: this.getContextMenuItems
            }
        },
        ENTITY_TYPE_PORTION() {
            return ATTACHMENT_ENTITY_TYPE.PORTION
        },
        ENTITY_TYPE_SUBPORTION() {
            return ATTACHMENT_ENTITY_TYPE.SUBPORTION
        },
        areAllPortionsHidden() {
            return this.portions.length === this.portions.filter(portion => !portion.showBlock).length
        },
        areAllPortionsExpanded() {
            return this.portions.length === this.portions.filter(portion => portion.showBlock).length
        }
    },
    watch: {
        activeArrow(newValue) {
            newValue ? this.expandAll() : this.hideAll()
        }
    },
    async mounted() {
        await this.reloadPortions()
        this.activeArrow = true
    },
    created() {
        this.broadCastAttachmentUpdateChannel = new BroadcastChannel(BROADCAST_ATTACHMENT_UPDATED_CHANNEL)
        this.broadCastAttachmentUpdateChannel.onmessage = () => {
            const selectedPortions = this.portions.filter(portion => portion.showBlock)
            this.reloadPortions(selectedPortions)
        }
    },
    beforeDestroy() {
        this.broadCastAttachmentUpdateChannel.close()
    },
    methods: {
        onSubportionAnalysisSidePanelClose() {
            this.visibleSubportionAnalysisSidePanel = false
        },
        onOpenAnalysisSidePanel(subportionData) {
            this.selectedSubportionAnalysisResult = subportionData.analysisResult
            this.selectedSubportionLabel = subportionData.label
            this.visibleSubportionAnalysisSidePanel = true
        },
        async reloadTab() {
            await this.loadPortions(true)
        },
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        onSubportionsTitleClick() {
            this.toggleActiveArrow()
        },
        onSubportionsTitleArrowClick() {
            this.toggleActiveArrow()
        },
        expandAll() {
            this.portions.forEach((portion) => { portion.showBlock = true })
        },
        hideAll() {
            this.portions.forEach((portion) => { portion.showBlock = false })
        },
        toggleActiveArrow() {
            this.activeArrow = !this.activeArrow
        },
        togglePortion(portion) {
            portion.showBlock = !portion.showBlock
            if (this.areAllPortionsExpanded) {
                this.activeArrow = true
            } else if (this.areAllPortionsHidden) {
                this.activeArrow = false
            }
        },
        async reloadPortions(selectedPortions) {
            await this.loadPortions()
            if (selectedPortions) {
                selectedPortions.forEach(portion => {
                    this.selectPortion(portion.id)
                })
            } else if (this.$route.params.porId) {
                this.selectPortion(this.$route.params.porId)
            }
        },
        selectPortion(id) {
            const portionToSelect = find(this.portions, { id })
            this.$nextTick(() => {
                portionToSelect.showBlock = true
            })
        },
        showInfoReviewModal(event) {
            this.$emit('showReviewInfoModal', event)
        },
        onShowReviewModal(portion, subportion) {
            const entitySelected = {
                id: subportion.id,
                portionId: portion.id,
                label: subportion.label,
                currentLaboratory: subportion.currentLaboratory
            }
            this.$emit('showReviewModal', [ entitySelected, this.ENTITY_TYPE_SUBPORTION ])
        },
        hasBeenChanged() {
            return false
        },
        async loadPortionsMaitainingOpened() {
            const oldPortions = this.portions
            await this.loadPortions()
            this.portions.forEach(portion => {
                const oldPortionFound = oldPortions.find(oldPortion => oldPortion.id === portion.id)
                if (oldPortionFound) {
                    portion.showBlock = oldPortionFound.showBlock
                }
            })
        },
        mapAndShowRevisionInfo(entity, entityType, revision) {
            const reviewInfoData = {
                revision: revision,
                entitySelected: entity,
                entityType: entityType
            }
            this.$emit('showReviewInfoModal', reviewInfoData)
        },
        onShowRevisionPopoverClick(entity, entityType) {
            if (entity.revisions.length === 1) {
                this.mapAndShowRevisionInfo(entity, entityType, entity.revisions[0])
            }
        },
        allEntityRevisionsCompleted(revisions) {
            return revisions.filter(revision => revision.finished).length === revisions.length
        },
        async loadPortions(showAllBlocks = false) {
            this.updateLoading(true)
            const [err, response] = await this.toCall(DiagnosisPortionService.getSamplePortions(this.labId, this.casId, this.samId))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            response.map((portion) => {
                portion.showBlock = showAllBlocks
                portion.subportions.sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }))
            })
            this.portions = response
        },
        async showReviewModal(entity, entityType) {
            this.$emit('showReviewModal', [ entity, entityType ])
        },
        async requestReview(portionId) {
            const [err] = await this.toCall(DiagnosisPortionService.requestReview(this.labId, this.casId, this.samId, portionId))
            if (err) {
                this.showErrorModalGlobal()
            } else {
                await this.loadPortions()
            }
        },
        showAssayModal(portionLabel) {
            this.$emit('showAssayModal', portionLabel)
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                const unfinishedPortionRevision = params?.node.data.portionRevisions.find(revision => !revision.finished)
                const unfinishedSubportionRevision = params?.node.data.subportionRevisions.find(revision => !revision.finished)
                return [
                    {
                        name: this.$t('diagnosis.labels.viewBlockReview'),
                        cssClasses: unfinishedPortionRevision ? '' : ['not-show'],
                        action: () => {
                            this.mapAndShowRevisionInfo(params?.node.data, this.ENTITY_TYPE_PORTION, unfinishedPortionRevision)
                        }
                    },
                    {
                        name: this.$t('diagnosis.labels.viewSlideReview'),
                        cssClasses: unfinishedSubportionRevision ? '' : ['not-show'],
                        action: () => {
                            this.mapAndShowRevisionInfo(params?.node.data, this.ENTITY_TYPE_SUBPORTION, unfinishedSubportionRevision)
                        }
                    },
                    {
                        name: this.$t('global.export'),
                        subMenu: [
                            'excelExport'
                        ]
                    },
                    {
                        name: this.$tc('global.technique', 2),
                        action: () => this.showAssayModal(params?.node.data.label)
                    },
                    {
                        name: this.$t('diagnosis.btns.incident'),
                        class: this.$_canCreateIncident() ? '' : 'd-none',
                        action: () => this.$emit('openIncidentFormModal', {
                            coreEntity: 'SUBPORTION',
                            entitiesIds: {
                                casId: this.casId,
                                samId: this.samId,
                                porId: params?.node.data.portionId,
                                supId: params?.node.data.id
                            }
                        })
                    },
                    {
                        name: this.$t('diagnosis.grid.subportionGrid.contextMenu.showIncidents'),
                        class: params?.node.data.incidentsCount ? '' : 'd-none',
                        action: () => this.$emit('showIncidentsModalForSubportion', {
                            coreEntity: 'SUBPORTION',
                            casId: this.casId,
                            samId: this.samId,
                            porId: params?.node.data.portionId,
                            supId: params?.node.data.id
                        })
                    },
                    {
                        name: this.$t('diagnosis.grid.subportionGrid.contextMenu.showAttachments'),
                        class: params?.node.data.attachmentsCount ? '' : 'd-none',
                        action: () => this.$emit('showSubportionAttachmentTreeModal', params?.node.data.id)
                    },
                    {
                        name: this.$t('diagnosis.grid.subportionGrid.contextMenu.addAttachment'),
                        class: this.$_canAttachDocumentToSubportion() && this.$_canBeDocumentAttachedToSubportion(params?.node.data) ? '' : 'd-none',
                        action: () => this.$emit('openAddFileModalForSubportion', [
                            this.labId,
                            this.casId,
                            this.samId,
                            params?.node.data.portionId,
                            params?.node.data.id
                        ])
                    },
                    {
                        name: this.$tc('global.showInGlobalLocator'),
                        action: () => this.showInGlobalLocator(params?.node.data.label)
                    }
                    // Oculto por petición de Javier
                    // {
                    //     name: this.$t('diagnosis.btns.qualityUse')
                    // }
                ]
            }
        },
        async onGridReady({ api }) {
            this.gridApi = api
            await this.setGridData()
        },

        async getAssaysForSample() {
            const response = await this.$_toCallUnique(subportionService.getAssaysForSample(this.labId, this.casId, this.samId))
            return response ?? []
        },

        async setGridData() {
            this.rowData = await this.getAssaysForSample()
        },

        canRequestReview(portionData) {
            return portionData.metadata.operationGuards.canCreateRevision && this.$_canRequestAnyPortionRevision()
        },
        async getPortionUnfinishedRevisions(portionId) {
            this.updateLoading(true)
            const [error, response] = await this.toCall(portionRevisionServices.getPortionUnfinishedRevision(this.casId, this.samId, portionId, this.labId))
            this.updateLoading(false)
            if (error) {
                if (error.response.status !== 404) {
                    this.showErrorModalGlobalFromErrorResponse(error)
                }
                this.portionHaveUnfinishedRevisions = false
                return
            }
            this.portionHaveUnfinishedRevisions = response
        }
    }
}
</script>

<style lang="scss">
    .section-subportions .btn-round.pending {
        color: vt-color(orange-medium);
    }
    .section-subportions .btn-round.completed {
        color: vt-color(green-radiant);
    }
    .empty-subportions-text {
        margin-left: 14px;
        font-style: italic;
        color: vt-color(dark-text, .5);
        font-size: font-size(normal);
    }
</style>

<style lang="scss" scoped>
.subportions-scroll{
  height: calc(100vh - 310px) !important;
  padding-right: 5px;
  overflow-y: auto;
  overflow-x: hidden;
}

.portion-block {
  cursor: pointer;
  transition: 0.2s;
  color: vt-color(primary);
}

.portion-block:hover {
  color: vt-color(dark-primary);
}

.portion-block .no-assays {
    color: vt-color(primary) !important;
    cursor: default !important;
}

.section-subportions .portion-block .btn.disabled {
    color: vt-color(base-text, .3) !important;
    cursor: default;
}
.arrow {
    transition-timing-function: ease;
    transition-duration: .1s;
    transition-property: transform;
}
.arrow.active {
    transform: rotate(90deg);
}
</style>
